'use strict';
/* ==========================================================================
   levels/bill.js — step "bill" (orientation any): PASS THE BILL (DESIGN.md §5.2).

   An attempt = 4 fictional bills from the pool content/bank.bill.json, drawn by the model
   (Civics.drawBills(json, {seed, avoid}): draw.require level1 / level2 / override, fresh items first, served easiest first)
   with 3 decisions each. EVERY vote count, threshold, path and outcome comes from core-local/civics.js (the bill's
   journey(), majorityNeeded / twoThirdsNeeded); the options and right answers are bank instances (the model's own
   askItem → core/bank.js instantiate). Nothing here types in a number or a verdict.

   The board: a flowchart across the top (NEW BILL → HOUSE (Capitol, navy) → SENATE (Capitol, navy) → PRESIDENT
   (White House, brick) → LAW (a signed scroll + red wax seal) or the BIN (the bill crumples into it); the override loop
   from the President back to both chambers; the Supreme Court pops in for a court case). The bill is a paper scroll
   with its title that travels along the arrows. Each vote is a TALLY BOARD (paper tiles, yes navy / no cream, filling
   over ~1 s, the counts in woodtype); the threshold line (majority or two-thirds) is drawn on it only AFTER the
   student answers (or when the verdict shows).

   A bill is played as a list of BEATS (beatsOf): unroll → for each journey stage: move → tally (a vote) | act
   (president / law / dead / court) → its asks (the item's `at`; "start" = before the House) → verdict (a vote).
   Timed beats run by themselves (Enter / Space / the ▶▶ button fast-forward); an ask waits for an answer.
   Wrong answer → the Judge's `why` line, and for a numeric card the step-by-step `explain` written on the board; one
   retry; a second miss shows the answer. Stars: right on the first try = 1; of = 12; pass = pool.passStars (9).

   Progress: Game.progress.bank.bill = {pool, seed, attempt, ids, prev, open, run: {b, k, picks, res}} (the same shape
   as core/bank.js attempts, so Bank.finish('bill') closes it and ?seed= / window.__bankSeed force a seed); a reload
   mid-attempt resumes the same bills at the same beat with the same picks. Game.progress.scores.bill = {best, last,
   of}; the first answer to each decision under Game.progress.answers['bill/<instance key>']. Pass → "Collect your
   stamp" (Game.complete('bill') → the SIGNED card); leaving a passed attempt without collecting still stamps.

   Portrait phone (Dock.active): the stage shows the flowchart large and a compact tally card; the question, the
   options (+ the Judge's note on a wrong one), the working and every control are dock buttons.
   Keys: 1–3 = option, Enter / Space = start / skip the animation / continue, Escape = the map (global).
   SFX when loaded: tally, unroll, crumple, cheer, gavel (else shared stand-ins); images IMG.capitol / whitehouse /
   court / scroll / gavel / quill through core-local/civic.js (paper stand-ins when missing).

   QA hooks: sc.phase ('intro' | 'unroll' | 'move' | 'tally' | 'act' | 'ask' | 'answered' | 'verdict' | 'billdone' |
   'end'), sc.current {bill, ask, askIndex, stage, beat}, sc.answerIndex(), sc.choose(i) → bool, sc.next() → bool,
   sc.skip() → bool (fast-forward timed beats), sc.begin(fresh), sc.collect(), sc.bills, sc.bi, sc.k, sc.beats,
   sc.picks, sc.results, sc.stars, sc.of, sc.passStars, sc.passed, sc.seed, sc.attempt, sc.resumed, sc.solution
   {answerIndex, answerText, stage, key}; Levels.bill.solution() (the live scene's), Levels.bill.beatsOf(bill),
   Levels.bill.options() (the canvas option rects). Integration compat: sc.done() / sc.finishStub() = end now + stamp.
   ========================================================================== */
(function () {
  const STEP = 'bill';
  const HAND = "'Caveat Brush'", SLAB = "'Alfa Slab One'", TYPE = "'Special Elite'";
  const CC = () => (window.Civic ? Civic.C : { navy: '#1f2f55', brick: '#9e2b25', cream: '#f3ead7', gold: '#b08a2e', parchment: '#efe2c2', ink: '#2b2420', kraft: '#c99a67' });
  const json = () => Game.content.bank_bill || { pool: STEP, draw: { n: 4 }, items: [] };
  const TS = () => (typeof window.__timeScale === 'number' && window.__timeScale > 0 ? window.__timeScale : 1);
  const DUR = { unroll: 1.1, move: 0.95, tally: 1.45, act: 1.6, verdict: 1.25 };
  const FILL = 1.0, NEXT_GUARD = 0.25;
  const PHASE = { unroll: 'unroll', move: 'move', tally: 'tally', act: 'act', verdict: 'verdict', ask: 'ask', done: 'billdone' };
  const VOTES = ['house', 'senate', 'override-house', 'override-senate'];
  const isVote = s => VOTES.includes(s);
  const chamberOf = s => (/senate/.test(s) ? 'senate' : 'house');
  const isOverride = s => /^override-/.test(s);
  const NODE = { start: 'start', house: 'house', 'override-house': 'house', senate: 'senate', 'override-senate': 'senate', president: 'president', law: 'law', dead: 'dead', court: 'law' };
  const ease = u => (u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2);
  const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;
  const stepsOf = s => String(s || '').split(/(?<=\.)\s+/).map(x => x.trim()).filter(Boolean);
  const SMALL = new Set(['for', 'of', 'the', 'a', 'an', 'and', 'to', 'in', 'on']);
  const titleCase = s => String(s).toLowerCase().split(' ').map((w, i) => (i && SMALL.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1))).join(' ');
  const has = n => typeof Audio !== 'undefined' && Audio.has && Audio.has(n);
  const numeric = inst => !!(inst && inst.item && inst.item.formula);

  /* ---------------- the attempt (core/bank.js's attempt shape, drawn by the model) ---------------- */
  function store() { const P = Game.progress; if (!P.bank || typeof P.bank !== 'object') P.bank = {}; return P.bank; }
  function drawAttempt(fresh) {
    const S = store(), st = S[STEP], forced = Bank.seedOverride();
    let seed, prev, no, resumed = false;
    if (!fresh && st && st.open && st.pool === STEP && (forced == null || String(forced) === String(st.seed))) { seed = st.seed; prev = st.prev || []; no = st.attempt || 1; resumed = true; }
    else { seed = forced != null ? forced : Bank.newSeed(); prev = (st && st.ids) || []; no = ((st && st.attempt) || 0) + 1; }
    const d = Civics.drawBills(json(), { seed, avoid: prev });
    const run = resumed && st ? st.run : undefined;
    S[STEP] = { pool: STEP, seed, attempt: no, ids: d.bills.map(b => b.id), prev, open: true };
    if (run) S[STEP].run = run;
    Game.progress.save();
    return { seed, attempt: no, bills: d.bills, resumed, run };
  }

  /* a bill as beats: unroll, [start asks], per stage: move, tally | act, its asks, [verdict]; done */
  function beatsOf(bill) {
    const out = [], asks = bill.asks.map((a, i) => ({ at: a.at, i })), used = new Set();
    let cur = null;
    const addAsks = stage => asks.forEach(x => { if (x.at === stage && !used.has(x.i)) { used.add(x.i); out.push({ kind: 'ask', stage, ask: x.i, st: cur }); } });
    out.push({ kind: 'unroll', stage: 'start', st: null });
    addAsks('start');
    for (const s of bill.journey.stages) {
      cur = s;
      out.push({ kind: 'move', stage: s.stage, st: s });
      out.push({ kind: isVote(s.stage) ? 'tally' : 'act', stage: s.stage, st: s });
      addAsks(s.stage);
      if (isVote(s.stage)) out.push({ kind: 'verdict', stage: s.stage, st: s });
    }
    // an `at` the journey never reaches (tools/civicscheck.js should refuse it): asked at the end rather than lost
    asks.forEach(x => { if (!used.has(x.i)) { used.add(x.i); out.push({ kind: 'ask', stage: x.at, ask: x.i, st: cur }); } });
    out.push({ kind: 'done', stage: 'done', st: cur });
    return out;
  }

  /* what the card says while a timed beat runs (every fact from the journey the model computed) */
  function narration(b, bill) {
    const st = b.st, name = 'The ' + titleCase(bill.title), J = bill.journey;
    const failed = J.stages.find(s => s.result === 'fail' || s.result === 'tie-vp-no');
    switch (b.kind) {
      case 'unroll': return `A new bill: ${name}.`;
      case 'move':
        return {
          house: 'Off to the House of Representatives (435 seats).', senate: 'Next stop: the Senate (100 seats).',
          president: "To the President's desk.", 'override-house': 'Back to the House for an override vote.',
          'override-senate': 'Now the Senate votes on the override.', law: 'On to the law books...',
          dead: 'Into the bin...', court: 'Someone sues, and the case goes to court.',
        }[b.stage] || '';
      case 'tally': return `The ${chamberOf(b.stage) === 'house' ? 'House' : 'Senate'} votes${isOverride(b.stage) ? ' on the override' : ''}...`;
      case 'verdict': {
        const ch = chamberOf(b.stage) === 'house' ? 'House' : 'Senate', v = st.yes + st.no;
        if (st.result === 'tie-vp-yes') return `A ${st.yes} to ${st.no} tie: the Vice President votes yes, so it passes the Senate.`;
        if (st.result === 'tie-vp-no') return `A ${st.yes} to ${st.no} tie: the Vice President votes no, so it fails.`;
        if (isOverride(b.stage)) return st.result === 'pass'
          ? `Override passes the ${ch}: ${st.yes} of ${v} is at least two-thirds (${Civics.twoThirdsNeeded(v)}).`
          : `The override fails in the ${ch}: ${st.yes} of ${v} is short of two-thirds (${Civics.twoThirdsNeeded(v)}).`;
        return st.result === 'pass' ? `It passes the ${ch}, ${st.yes} to ${st.no}.` : `It fails in the ${ch}, ${st.yes} to ${st.no}.`;
      }
      case 'act':
        if (b.stage === 'president') return {
          sign: 'The President signs it.', veto: 'The President vetoes it!',
          'wait-in-session': 'The President does nothing for ten days (Sundays excepted), and Congress is in session.',
          'wait-adjourned': 'The President does nothing, and Congress adjourns before the ten days are up.',
        }[st.result] || '';
        if (b.stage === 'law') return J.path.includes('override-senate:pass') ? "It's a law: Congress overrode the veto." : J.path.includes('president:wait-in-session') ? "It's a law, without the President's signature." : "It's a law!";
        if (b.stage === 'dead') {
          if (J.path.includes('president:wait-adjourned')) return 'A pocket veto: the bill is dead.';
          if (failed && isOverride(failed.stage)) return 'The veto stands. The bill is dead.';
          if (failed) return `It failed in the ${chamberOf(failed.stage) === 'house' ? 'House' : 'Senate'}, so the bill is dead.`;
          return 'The bill is dead.';
        }
        if (b.stage === 'court') return st.result === 'struck' ? 'The Supreme Court strikes the law down: it breaks the Constitution.' : 'The Supreme Court upholds the law.';
        return '';
      default: return '';
    }
  }

  /* ---------------- layout ---------------- */
  const X = { start: 175, house: 520, senate: 862, president: 1204, law: 1548, court: 1792 };
  function layout(docked) {
    const d = !!docked, base = d ? 560 : 392, bh = d ? 230 : 172, lab = d ? 46 : 30, ss = d ? 1.3 : 0.82;
    return { d, base, bh, lab, ss, lawY: base - bh * 0.93, binY: base + (d ? -4 : 6), markY: base + lab * 0.85 + lab * 1.05, mark2Y: base + lab * 0.85 + lab * 2.05 };
  }
  function spot(F, node) {
    switch (node) {
      case 'start': return { x: X.start, y: F.base - F.bh * 0.42 };
      case 'house': case 'senate': case 'president': return { x: X[node], y: F.base - F.bh * 0.46 };
      case 'law': return { x: X.law, y: F.lawY };
      case 'dead': return { x: X.law, y: F.binY - F.bh * 0.52 };
      default: return { x: X.start, y: F.base - F.bh * 0.42 };
    }
  }
  const LIFT = { 'override-house': 1.25, 'override-senate': 0.3, law: 0.45, dead: 0.2, president: 0.25, senate: 0.25, house: 0.25 };

  /* ---------------- pieces ---------------- */
  const _lines = new Map();
  function titleLines(title, size, maxW) {
    const k = title + '|' + size + '|' + maxW;
    if (!_lines.has(k)) _lines.set(k, UI._wrap(title, maxW, `${size}px ${TYPE}`));
    return _lines.get(k);
  }
  /* the bill: a parchment sheet between two rolled ends, its title typed on */
  function scroll(x, y, s, title, { rot = 0, alpha = 1, sign = 0, seal = 0, struck = 0 } = {}) {
    if (s <= 0.001 || alpha <= 0) return;
    const c = CC(), w = 236, h = 124;
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s); g.globalAlpha *= alpha;
    paperRect(0, 0, w, h, c.parchment, { seed: 71, depth: 1.1 });
    paperRect(-w / 2, 0, 24, h + 20, '#e2cf9f', { seed: 72, depth: 0.7 });
    paperRect(w / 2, 0, 24, h + 20, '#e2cf9f', { seed: 73, depth: 0.7 });
    g.textAlign = 'center'; g.textBaseline = 'middle';
    g.font = `15px ${TYPE}`; g.fillStyle = c.brick; g.fillText('A BILL', 0, -h / 2 + 17);
    const L = titleLines(title, 21, w - 60);
    g.font = `21px ${TYPE}`; g.fillStyle = c.ink;
    L.slice(0, 3).forEach((l, i) => g.fillText(l, 0, -8 + (i - (Math.min(3, L.length) - 1) / 2) * 23 + 8));
    if (sign > 0) ink([[-70, h / 2 - 16], [-50, h / 2 - 28], [-38, h / 2 - 12], [-20, h / 2 - 30], [0, h / 2 - 14], [22, h / 2 - 24], [44, h / 2 - 16]], { w: 3, color: c.navy, seed: 74, prog: sign, step: 6, passes: 1 });
    if (seal > 0) Civic.seal(w / 2 - 34, h / 2 - 16, 30, { text: 'LAW', seed: 75, s: seal, rot: -0.2 });
    if (struck > 0) { ink([[-w / 2 + 20, -h / 2 + 14], [w / 2 - 20, h / 2 - 14]], { w: 6, color: c.brick, seed: 76, prog: struck, step: 10 }); ink([[w / 2 - 20, -h / 2 + 14], [-w / 2 + 20, h / 2 - 14]], { w: 6, color: c.brick, seed: 77, prog: clamp(struck * 2 - 1), step: 10 }); }
    g.restore();
  }
  const _ball = Object.assign(blobPts(40, 36, 91, 0.22, 16), { k: 'bill:ball' });
  function ball(x, y, s, rot) {
    if (s <= 0.001) return;
    paperPiece(_ball, x, y, CC().parchment, { s, rot, seed: 92, depth: 0.9 });
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
    ink([[-22, -10], [-4, 4], [14, -14]], { w: 2, color: 'rgba(90,60,30,0.6)', seed: 93, passes: 1, step: 8 });
    ink([[-12, 18], [6, 6], [24, 14]], { w: 2, color: 'rgba(90,60,30,0.6)', seed: 94, passes: 1, step: 8 });
    g.restore();
  }
  const _bin = Object.assign([[-56, -70], [56, -70], [42, 0], [-42, 0]], { k: 'bill:bin' });
  function bin(x, y, s) {
    paperPiece(_bin, x, y, '#7a5a3c', { s, seed: 95, depth: 1 });
    g.save(); g.translate(x, y); g.scale(s, s);
    for (let i = -2; i <= 2; i++) ink([[i * 20, -64], [i * 15, -6]], { w: 2.5, color: 'rgba(30,20,12,0.55)', seed: 96 + i, passes: 1, step: 20 });
    paperRect(0, -70, 124, 12, '#5e4430', { seed: 99, depth: 0.4 });
    g.restore();
  }
  function label(text, x, y, size, { color, fill = null, ink: inkC = null } = {}) {
    g.save(); g.font = `${size}px ${SLAB}`; g.textAlign = 'center'; g.textBaseline = 'middle';
    if (fill) { const w = Math.round(g.measureText(text).width + size * 0.9); paperRect(x, y, w, Math.round(size * 1.45), fill, { seed: 60 + text.length, depth: 0.6, boil: false }); g.restore(); g.save(); g.font = `${size}px ${SLAB}`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = inkC || PAL.cream; }
    else g.fillStyle = color;
    g.fillText(text, x, y + size * 0.05);
    g.restore();
  }
  const sfx = (sc, name, fb, o = {}) => { if (sc.quiet) return; if (has(name)) Audio.play(name, o); else if (fb) Audio.play(fb[0], { gain: fb[1], rate: fb[2] || 1 }); };

  /* ---------------- the scene ---------------- */
  class Bill extends Scene {
    get json() { return json(); }
    get n() { return this.bills ? this.bills.length : (this.json.draw && this.json.draw.n) || 4; }
    get of() { return this.bills ? this.bills.reduce((a, b) => a + b.asks.length, 0) : this.n * 3; }
    get passStars() { return Math.min(this.of, this.json.passStars || Math.ceil(this.of * 3 / 4)); }
    get bill() { return this.bills ? this.bills[this.bi] : null; }
    get beat() { return this.beats ? this.beats[this.k] : null; }
    get inst() { const b = this.beat; return b && b.kind === 'ask' && this.bill ? this.bill.asks[b.ask] : null; }
    get current() { const b = this.beat; return this.bill && b ? { bill: this.bill, ask: this.inst, askIndex: b.kind === 'ask' ? b.ask : -1, stage: b.stage, beat: b.kind } : null; }
    get solution() { const q = this.inst; return q ? { answerIndex: q.answerIndex, answerText: q.options[q.answerIndex], stage: this.beat.stage, key: q.key } : null; }
    get stars() { return (this.results || []).reduce((a, r) => a + (r || []).reduce((b, x) => b + (x ? x.stars : 0), 0), 0); }
    answerIndex() { const q = this.inst; return q ? q.answerIndex : -1; }
    res(bi = this.bi, ai) { const r = this.results && this.results[bi]; return r ? r[ai] : null; }

    enter() {
      this.phase = 'intro'; this.bills = null; this.bi = 0; this.k = 0; this.beats = null; this.results = []; this.picks = [];
      this.passed = false; this.collected = false; this.quiet = false;
      Audio.play('paper', { gain: 0.5 });
      const st = Game.progress.bank && Game.progress.bank[STEP];
      if (st && st.open && st.run && st.pool === STEP) this.begin();          // an attempt in progress: straight back in
    }
    exit() { this.bankStamp(); }

    /* ---- attempt ---- */
    begin(fresh = false) {
      this.bankStamp();
      const a = drawAttempt(fresh);
      this.bills = a.bills; this.seed = a.seed; this.attempt = a.attempt; this.resumed = a.resumed;
      this.results = this.bills.map(() => []); this.passed = false; this.collected = false;
      if (a.resumed && a.run && this.restore(a.run)) return;
      this.startBill(0);
    }
    restore(run) {
      try {
        if (!(run.b >= 0 && run.b < this.bills.length)) return false;
        this.results = this.bills.map((b, i) => (run.res && run.res[i] ? run.res[i].slice() : []));
        this.bi = run.b; this.beats = beatsOf(this.bill); this.lineAt = {};
        const k = Math.max(0, Math.min(this.beats.length - 1, run.k | 0));
        this.quiet = true;
        this.startBeat(k);
        if (this.phase === 'ask') {
          const r = this.res(this.bi, this.beat.ask);
          if (r) { this.picks = r.picks.slice(); this.phase = 'answered'; this.answeredAt = this.pickedAt = this.workAt = Game.time - 5; }
          else for (const p of run.picks || []) this.choose(p);
          if (this.workAt != null) this.workAt = Game.time - 5;
        }
        this.quiet = false;
        this.save();
        return true;
      } catch (e) { this.quiet = false; console.warn('[bill] could not resume: ' + e.message); return false; }
    }
    startBill(i) {
      this.bi = i; this.beats = beatsOf(this.bill); this.lineAt = {};
      if (!this.results[i]) this.results[i] = [];
      this.startBeat(0);
    }
    startBeat(k) {
      this.k = k; this.beatAt = Game.time; this.ticks = 0;
      const b = this.beat;
      this.phase = PHASE[b.kind]; this.picks = []; this.pickedAt = null; this.answeredAt = null; this.workAt = null;
      const st = b.st;
      if (b.kind === 'unroll') sfx(this, 'unroll', ['paper', 0.55, 0.9]);
      else if (b.kind === 'move') sfx(this, 'whoosh', null, { gain: 0.25, rate: 1.1 });
      else if (b.kind === 'act') {
        if (b.stage === 'president') { if (st.result === 'veto') sfx(this, 'stamp', null, { gain: 0.8 }); else if (st.result === 'sign') sfx(this, 'scribble', null, { gain: 0.45 }); else sfx(this, 'paper', null, { gain: 0.5 }); }
        else if (b.stage === 'law') sfx(this, 'cheer', ['sparkle', 0.7], { gain: 0.7 });
        else if (b.stage === 'dead') sfx(this, 'crumple', ['paper', 0.7, 0.6], { gain: 0.8 });
        else if (b.stage === 'court') sfx(this, 'gavel', ['thunk', 0.8, 0.8], { gain: 0.8 });
      } else if (b.kind === 'verdict') {
        const pass = st.result === 'pass' || st.result === 'tie-vp-yes';
        sfx(this, 'gavel', ['stamp', 0.6], { gain: 0.6 });
        if (!pass) sfx(this, 'thunk', null, { gain: 0.3, rate: 0.8 });
      } else if (b.kind === 'done') sfx(this, 'paper', null, { gain: 0.5 });
      this.save();
    }
    advance() { if (this.k < this.beats.length - 1) this.startBeat(this.k + 1); }
    save() {
      const S = Game.progress.bank && Game.progress.bank[STEP];
      if (S && S.open && this.phase !== 'end' && this.beats) S.run = { b: this.bi, k: this.k, picks: this.picks.slice(), res: this.results.map(r => (r || []).slice()) };
      Game.progress.save();
    }

    /* ---- answering ---- */
    choose(i) {
      const q = this.inst;
      if (this.phase !== 'ask' || !q || !(i >= 0 && i < q.options.length) || this.picks.includes(i)) return false;
      if (!this.picks.length) Game.progress.answer(STEP + '/' + q.key, q.options[i]);            // first try only
      this.picks.push(i); this.pickedAt = Game.time;
      const right = i === q.answerIndex;
      if (right || this.picks.length >= 2) {
        const stars = right && this.picks.length === 1 ? 1 : 0;
        this.results[this.bi][this.beat.ask] = { key: q.key, stars, picks: this.picks.slice(), right, revealed: !right };
        this.phase = 'answered'; this.answeredAt = Game.time;
        if (this.workAt == null && numeric(q)) this.workAt = Game.time;
        if (right) sfx(this, 'ding', null, { gain: stars ? 0.8 : 0.5 }); else sfx(this, 'wahwah', null, { gain: 0.3 });
      } else {
        if (numeric(q)) this.workAt = Game.time;
        sfx(this, 'thunk', null, { gain: 0.4, rate: 0.85 });
      }
      this.save();
      return true;
    }
    /* Continue (after an answer) / Next bill (after a bill) */
    next() {
      if (this.phase === 'answered' && Game.time - this.answeredAt >= NEXT_GUARD) { this.advance(); return true; }
      if (this.phase === 'billdone' && Game.time - this.beatAt >= NEXT_GUARD) {
        if (this.bi < this.bills.length - 1) this.startBill(this.bi + 1); else this.finish();
        return true;
      }
      return false;
    }
    /* fast-forward the running animations to the next thing that needs the student */
    skip() {
      let moved = false;
      while (this.beat && DUR[this.beat.kind] && this.k < this.beats.length - 1) { this.quiet = true; this.advance(); moved = true; }
      this.quiet = false;
      if (moved) this.beatAt = Math.min(this.beatAt, Game.time - 0.6);      // the beat we land on shows at once
      return moved;
    }
    finish() {
      const s = this.stars;
      this.phase = 'end'; this.endAt = Game.time; this.passed = s >= this.passStars;
      const S = Game.progress.bank && Game.progress.bank[STEP]; if (S) delete S.run;
      Bank.finish(STEP);                                                             // the next attempt draws new bills
      const P_ = Game.progress, prev = P_.scores[STEP];
      P_.scores[STEP] = { best: Math.max((prev && prev.best) || 0, s), last: s, of: this.of };
      P_.save();
      if (this.passed) sfx(this, 'cheer', ['sparkle', 0.8], { gain: 0.8 }); else Audio.play('paper');
    }
    bankStamp() { if (this.passed && !this.collected) { this.collected = true; Game.progress.stamps[STEP] = true; Game.progress.save(); Game._justStamped = STEP; } }
    collect() { if (this.collected) return; this.collected = true; Game.complete(STEP); }
    /* tests only (tools/integration.js): end the attempt now with the stars so far and stamp the step */
    done() {
      if (!this.bills) this.begin();
      if (this.phase !== 'end') this.finish();
      this.collected = true; Game.complete(STEP);
    }
    finishStub() { this.done(); }
    endButtons() {
      return this.passed
        ? [{ label: 'Collect your stamp', color: CC().brick, go: () => this.collect() }, { label: 'Try again (new bills)', color: CC().navy, go: () => this.begin(true) }]
        : [{ label: 'Try again (new bills)', color: CC().brick, go: () => this.begin(true) }, { label: 'Back to the map', color: CC().navy, go: () => Game.replace(new Levels.map()) }];
    }

    update() {
      const b = this.beat;
      if (!b || !DUR[b.kind] || this.phase === 'end' || this.phase === 'intro') return;
      const el = (Game.time - this.beatAt) * TS();
      if (b.kind === 'tally') {
        const want = Math.min(8, Math.floor(el / FILL * 8));
        while (this.ticks < want) { this.ticks++; if (has('tally')) Audio.play('tally', { gain: 0.35, rate: 0.9 + this.ticks * 0.03 }); else if (this.ticks % 2) Audio.play('pop', { gain: 0.12, rate: 1.6 }); }
      }
      if (el >= DUR[b.kind]) this.advance();
    }
    keyDown(k) {
      const n = parseInt(k, 10), go = k === 'Enter' || k === ' ';
      if (this.phase === 'intro') { if (go) this.begin(); }
      else if (this.phase === 'ask') { if (n >= 1 && n <= 3) this.choose(n - 1); }
      else if (this.phase === 'answered' || this.phase === 'billdone') { if (go) this.next(); }
      else if (this.phase === 'end') { if (k === 'Enter') this.endButtons()[0].go(); }
      else if (go) this.skip();
    }

    /* ---------------- what the board shows at this beat (a pure function of the beats, the time and the answers) ---- */
    visual() {
      const B = this.beats, k = this.k, bt = this.beatAt, ts = TS(), res = this.results[this.bi] || [];
      const V = { at: 'start', from: 'start', moveP: 1, moveStage: null, tally: null, acts: {}, verdicts: {}, lines: {}, court: null };
      for (let j = 0; j <= k; j++) {
        const b = B[j], cur = j === k, prog = cur && DUR[b.kind] ? clamp((T - bt) * ts / DUR[b.kind]) : 1, t0 = cur ? bt : -1e6;
        if (b.kind === 'move') { V.from = V.at; V.at = NODE[b.stage]; V.moveP = cur ? ease(prog) : 1; V.moveStage = b.stage; if (b.stage === 'court') V.court = t0; }
        else if (b.kind === 'tally') V.tally = { st: b.st, p: cur ? clamp((T - bt) * ts / FILL) : 1 };
        else if (b.kind === 'act') V.acts[b.stage] = { p: prog, t0, st: b.st };
        else if (b.kind === 'verdict') { V.verdicts[b.stage] = { p: prog, t0, cur }; if (!V.lines[b.stage]) V.lines[b.stage] = { t0 }; }
        else if (b.kind === 'ask' && res[b.ask] && isVote(b.stage) && !V.lines[b.stage]) V.lines[b.stage] = { t0: cur ? this.answeredAt : -1e6 };
      }
      return V;
    }
    showWork() { return this.workAt != null && numeric(this.inst); }
    whyText() {
      const q = this.inst; if (!q) return '';
      const wrong = this.picks.filter(p => p !== q.answerIndex);
      return wrong.length ? q.why[wrong[wrong.length - 1]] || '' : '';
    }
    resultText() {
      const q = this.inst, r = q && this.res(this.bi, this.beat.ask);
      if (!r) return '';
      if (r.stars) return 'Right! +1 star.';
      if (r.right) return 'Right, on the second try. No star this time.';
      return `The answer: ${q.options[q.answerIndex]}. No star this time.`;
    }
    sub() {
      if (!this.bills || this.phase === 'intro' || this.phase === 'end') return '';
      return `bill ${this.bi + 1} of ${this.n} · ${plural(this.stars, 'star')}`;
    }

    /* ---------------- drawing ---------------- */
    draw() {
      const docked = UI.docked();
      g.fillStyle = paperTile(PAL.kraft, 3, 'tex_kraft'); g.fillRect(0, 0, Game.W, Game.H);
      if (this.phase === 'intro') return this.drawIntro(docked);
      if (this.phase === 'end') return this.drawEnd(docked);
      const F = layout(docked), V = this.visual();
      Civic.parchment(960, docked ? 390 : 300, 1900, docked ? 640 : 420, { seed: 801, depth: 1.2 });
      UI.hud('PASS THE BILL', this.sub());
      this.drawFlow(F, V);
      if (docked) { this.drawTallyCompact(V); this.drawDock(); return; }
      this.drawBoard({ x: 34, y: 514, w: 850, h: 552 }, V);
      this.drawCard(V);
    }

    /* the flowchart: arrows, buildings, labels, marks, LAW / bin / court, the travelling bill */
    drawFlow(F, V) {
      const c = CC(), bill = this.bill, J = bill.journey, path = J.path;
      const veto = path.includes('president:veto'), courtBill = J.stages.some(s => s.stage === 'court');
      const aw = F.d ? 7 : 5, ay = F.base - F.bh * 0.32, half = F.bh * 0.66;
      inkArrow(X.start + 118 * F.ss, ay, X.house - half, ay, { w: aw, color: c.ink, seed: 811 });
      inkArrow(X.house + half, ay, X.senate - half, ay, { w: aw, color: c.ink, seed: 812 });
      inkArrow(X.senate + half, ay, X.president - half, ay, { w: aw, color: c.ink, seed: 813 });
      inkArrow(X.president + half, F.base - F.bh * 0.62, X.law - 124 * F.ss, F.lawY + 10, { w: aw, color: c.ink, seed: 814, bulge: -12 });
      g.save(); g.globalAlpha *= 0.55; inkArrow(X.president + half, F.base - F.bh * 0.12, X.law - 68 * F.ss, F.binY - F.bh * 0.3, { w: aw * 0.8, color: c.ink, seed: 815, bulge: 10 }); g.restore();
      // the override loop: from the President back over the Senate to the House (two-thirds of each chamber)
      g.save(); g.globalAlpha *= veto ? 1 : 0.35;
      const ly = F.base - F.bh - 8;
      inkArrow(X.president - 24, ly, X.house + 24, ly, { bulge: F.bh * 0.55, w: aw, color: c.brick, seed: 816 });
      hand('OVERRIDE: TWO-THIRDS OF EACH CHAMBER', X.senate, ly - F.bh * 0.28 - (F.d ? 22 : 14), F.d ? 40 : 26, -1e6, { dur: 0.01, color: c.brick, seed: 817 });
      g.restore();
      // the buildings
      const on = n => V.at === n;
      Civic.building('capitol', X.house, F.base, F.bh, { seed: 821 });
      Civic.building('capitol', X.senate, F.base, F.bh, { seed: 822 });
      Civic.building('whitehouse', X.president, F.base, F.bh * 0.9, { seed: 823 });
      Civic.tool('quill', X.start - 100 * F.ss, F.base - F.bh * 0.5, F.bh * 0.5, { rot: -0.45, seed: 824 });
      const ly2 = F.base + F.lab * 0.85;
      label('NEW BILL', X.start, ly2, F.lab * 0.8, on('start') ? { fill: c.ink } : { color: c.ink });
      label('HOUSE', X.house, ly2, F.lab, on('house') ? { fill: c.navy } : { color: c.navy });
      label('SENATE', X.senate, ly2, F.lab, on('senate') ? { fill: c.navy } : { color: c.navy });
      label('PRESIDENT', X.president, ly2, F.lab * 0.92, on('president') ? { fill: c.brick } : { color: c.brick });
      // LAW slot and the bin
      const sealedAct = V.acts.law;
      if (!sealedAct) {
        g.save(); g.globalAlpha *= 0.5;
        if (IMG.scroll) cut('scroll', X.law, F.lawY, 96 * F.ss, { seed: 825, depth: 0.6 });
        else paperRect(X.law, F.lawY, Math.round(236 * F.ss * 0.8), Math.round(124 * F.ss * 0.8), c.parchment, { seed: 826, depth: 0.3, outline: true, boil: false });
        g.restore();
      }
      label('LAW', X.law, F.lawY - 62 * F.ss - F.lab * 0.9, F.lab, sealedAct ? { fill: c.navy } : { color: c.navy });
      bin(X.law, F.binY, F.d ? 1.45 : 1);
      if (V.acts.dead) label('DEAD', X.law, F.binY + F.lab * 0.85, F.lab, { fill: c.brick });
      // the Supreme Court (only once this bill's case reaches it)
      if (courtBill && V.court != null) {
        const s = pop(T, V.court, 0.4, 2);
        Civic.building('court', X.court, F.base, F.bh * 0.85, { s, seed: 827 });
        inkArrow(X.law + 120 * F.ss, F.lawY + 20, X.court - F.bh * 0.5, F.base - F.bh * 0.55, { w: aw, color: c.gold, seed: 828, bulge: -20, prog: P(T, V.court, V.court + 0.5) });
        if (s > 0.5) label('COURT', X.court, ly2, F.lab * 0.9, { color: '#7d6220' });
      }
      this.drawMarks(F, V);
      this.drawBill(F, V);
      this.drawStamps(F, V);
    }
    /* the result under each node: the votes (once shown), the President's act */
    drawMarks(F, V) {
      const c = CC(), J = this.bill.journey, fs = F.d ? 40 : 28;
      for (const s of J.stages) {
        if (isVote(s.stage)) {
          if (!V.lines[s.stage] && !V.verdicts[s.stage]) continue;
          const pass = s.result === 'pass' || s.result === 'tie-vp-yes';
          const txt = (isOverride(s.stage) ? 'override ' : '') + (pass ? 'passed ' : 'failed ') + s.yes + ' to ' + s.no + (s.result.startsWith('tie') ? ' + VP' : '');
          hand(txt, X[chamberOf(s.stage)], isOverride(s.stage) ? F.mark2Y : F.markY, fs, -1e6, { dur: 0.01, color: pass ? c.navy : c.brick, seed: 830 + s.stage.length });
        } else if (s.stage === 'president' && V.acts.president && V.acts.president.p > 0.45) {
          const txt = { sign: 'signed', veto: 'VETOED', 'wait-in-session': '10 days: law', 'wait-adjourned': 'pocket veto' }[s.result];
          hand(txt, X.president, F.markY, fs, -1e6, { dur: 0.01, color: s.result === 'sign' || s.result === 'wait-in-session' ? c.navy : c.brick, seed: 840 });
        }
      }
    }
    /* the bill: travelling, signed, sealed, crumpled into the bin, struck down */
    drawBill(F, V) {
      const bill = this.bill, a = spot(F, V.from), b = spot(F, V.at), u = V.moveP;
      const lift = (LIFT[V.moveStage] || 0.25) * F.bh;
      const cx = (a.x + b.x) / 2, cy = Math.min(a.y, b.y) - lift;
      let x = (1 - u) * (1 - u) * a.x + 2 * (1 - u) * u * cx + u * u * b.x, y = (1 - u) * (1 - u) * a.y + 2 * (1 - u) * u * cy + u * u * b.y;
      const J = bill.journey, pres = V.acts.president, law = V.acts.law, dead = V.acts.dead, court = V.acts.court;
      const signP = pres && pres.st.result === 'sign' ? P(pres.p, 0.15, 0.7) : 0;
      let s = F.ss, rot = Math.sin(u * Math.PI) * 0.12;
      const un = this.beat.kind === 'unroll' ? clamp((T - this.beatAt) * TS() / 0.6) : 1;
      if (dead) {
        const p = dead.p;
        if (p < 0.45) { const q = p / 0.45; scroll(x, y, s * (1 - q * 0.7), bill.title, { rot: q * 2.4, alpha: 1 - clamp((q - 0.7) / 0.3), sign: signP ? 1 : 0 }); }
        if (p > 0.32) { const q = clamp((p - 0.32) / 0.5), bx = X.law, by = F.binY - F.bh * 0.2 * (F.d ? 1.45 : 1) - 34 * (F.d ? 1.3 : 1); ball(bx, y + (by - y) * q * q, (F.d ? 1.4 : 1) * pop(p, 0.32, 0.2, 2), p * 5); }
        return;
      }
      if (V.at === 'start') { s *= 0.3 + 0.7 * E.outBack(un, 1.4); }
      scroll(x, y, s, bill.title, { rot, sign: signP, seal: law ? pop(law.p, 0.25, 0.3, 2.4) : 0, struck: court && court.st.result === 'struck' ? P(court.p, 0.45, 0.8) : 0 });
    }
    drawStamps(F, V) {
      const c = CC(), k = F.d ? 1.35 : 1, pres = V.acts.president;
      if (pres) {
        const r = pres.st.result, sp = pop(pres.p, 0.12, 0.2, 2);
        if (r === 'veto') UI.stamp('VETO', X.president, F.base - F.bh * 0.62, { s: sp * 0.52 * k, rot: -0.16, color: c.brick });
        if (r === 'wait-in-session' || r === 'wait-adjourned') {
          const cx = X.president + F.bh * 0.66, cy = F.base - F.bh * 1.08;
          g.save(); g.translate(cx, cy); g.scale(sp * k, sp * k); g.rotate(0.06);
          paperRect(0, 0, 138, 104, PAL.white, { seed: 850, depth: 1 }); paperRect(0, -40, 138, 26, c.brick, { seed: 851, depth: 0 });
          g.font = `44px ${SLAB}`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = c.ink; g.fillText('10', 0, 6);
          g.font = `16px ${TYPE}`; g.fillText('DAYS', 0, 38); g.font = `14px ${TYPE}`; g.fillStyle = PAL.cream; g.fillText('NOT SUNDAYS', 0, -39);
          g.restore();
          if (r === 'wait-adjourned') UI.stamp('ADJOURNED', X.president, F.base - F.bh * 0.5, { s: pop(pres.p, 0.45, 0.2, 2) * 0.4 * k, rot: -0.1, color: c.brick });
        }
      }
      const court = V.acts.court;
      if (court) {
        const hit = P(court.p, 0.1, 0.35), sw = hit < 1 ? -0.9 + hit * 0.9 : 0;
        Civic.tool('gavel', X.court - F.bh * 0.25, F.base - F.bh * 1.02, F.bh * 0.45, { rot: sw - 0.3, seed: 855 });
        if (court.st.result === 'struck') UI.stamp('STRUCK DOWN', X.law, F.lawY + 6, { s: pop(court.p, 0.4, 0.2, 2) * 0.42 * k, rot: -0.14, color: c.brick });
      }
      for (const [stage, v] of Object.entries(V.verdicts)) {
        if (!v.cur) continue;
        const st = this.beat.st, pass = st.result === 'pass' || st.result === 'tie-vp-yes';
        UI.stamp(pass ? 'PASSED' : 'FAILED', X[chamberOf(stage)], F.base - F.bh * 0.58, { s: pop(v.p, 0.05, 0.18, 2) * 0.58 * k * (1 - P(v.p, 0.85, 1)), rot: -0.12, color: pass ? c.navy : c.brick });
      }
    }

    /* ---- the tally board (desktop / landscape) ---- */
    drawBoard(R, V) {
      const c = CC();
      paperRect(R.x + R.w / 2, R.y + R.h / 2, R.w, R.h, '#a97c4f', { seed: 860, depth: 1.3, boil: false });
      if (this.showWork()) return this.drawWork(R);
      const t = V.tally;
      if (!t) {
        Civic.parchment(R.x + R.w / 2, R.y + R.h / 2, R.w - 60, R.h - 70, { seed: 861, lines: 0, depth: 0.8 });
        label('THE BILL', R.x + R.w / 2, R.y + 90, 40, { color: c.brick });
        const L = UI._wrap(this.bill.title, R.w - 160, `52px ${TYPE}`);
        g.save(); g.font = `52px ${TYPE}`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = c.ink;
        L.forEach((l, i) => g.fillText(l, R.x + R.w / 2, R.y + R.h / 2 - 20 + (i - (L.length - 1) / 2) * 62)); g.restore();
        hand('Waiting for the House vote.', R.x + R.w / 2, R.y + R.h - 80, UI.text(34), -1e6, { dur: 0.01, color: c.navy, seed: 862 });
        return;
      }
      const st = t.st, ch = chamberOf(st.stage), ov = isOverride(st.stage), yes = st.yes, no = st.no, N = yes + no;
      label((ch === 'house' ? 'HOUSE' : 'SENATE') + (ov ? ' OVERRIDE' : '') + ' VOTE', R.x + R.w / 2, R.y + 36, 30, { color: PAL.cream });
      const rows = ch === 'house' ? 15 : 10, cols = Math.ceil(N / rows);
      const gh = R.h - 196, gw = R.w - 90, sz = Math.floor(Math.min(gh / rows, gw / cols));
      const gx = Math.round(R.x + (R.w - cols * sz) / 2), gy = R.y + 68;
      const ys = Math.min(yes, Math.floor(t.p * yes + 1e-9)), ns = Math.min(no, Math.floor(t.p * no + 1e-9));
      this.tiles(gx, gy, sz, rows, yes, no, ys, ns);
      // the threshold line (after the answer / at the verdict)
      const ln = V.lines[st.stage];
      if (ln) {
        const need = ov ? Civics.twoThirdsNeeded(N) : Civics.majorityNeeded(N), cc = Math.floor(need / rows), r = need % rows;
        const top = gy - 12, bot = gy + rows * sz + 12;
        const pts = r === 0 ? [[gx + cc * sz, top], [gx + cc * sz, bot]] : [[gx + (cc + 1) * sz, top], [gx + (cc + 1) * sz, gy + r * sz], [gx + cc * sz, gy + r * sz], [gx + cc * sz, bot]];
        const pr = P(T, ln.t0, ln.t0 + 0.5);
        ink(pts, { w: 6, color: c.brick, seed: 870, prog: pr, step: 8, passes: 1 });
        const lx = Math.max(R.x + 150, Math.min(R.x + R.w - 150, gx + cc * sz));
        hand((ov ? 'two-thirds: ' : 'majority: ') + need, lx, bot + 20, UI.text(30), ln.t0 + 0.3, { color: PAL.cream, seed: 871, outline: c.brick });
      }
      // the Vice President's tie-breaking vote
      if (st.result && st.result.startsWith('tie') && (ln || V.verdicts[st.stage])) {
        const vx = gx + cols * sz + 58, vy = gy + rows * sz / 2;
        paperRect(vx, vy, 74, 74, c.brick, { seed: 872, depth: 0.8 });
        g.save(); g.font = `26px ${SLAB}`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = PAL.cream; g.fillText('VP', vx, vy - 12);
        g.font = `20px ${TYPE}`; g.fillText(st.result === 'tie-vp-yes' ? 'YES' : 'NO', vx, vy + 18); g.restore();
      }
      const cy = R.y + R.h - 66;
      Civic.woodtype('YES ' + ys, R.x + R.w * 0.3, cy, 40, { color: c.navy, ink: PAL.cream, seed: 873 });
      Civic.woodtype('NO ' + ns, R.x + R.w * 0.7, cy, 40, { color: c.cream, ink: c.navy, seed: 874 });
      hand(`${N} voting (of ${Civics.CHAMBERS[ch]} seats)`, R.x + R.w / 2, R.y + R.h - 22, UI.text(26), -1e6, { dur: 0.01, color: PAL.cream, seed: 875 });
    }
    /* the paper tiles: yes fills from the left (navy), no from the right (cream), column by column */
    tiles(gx, gy, sz, rows, yes, no, ys, ns) {
      const c = CC(), N = yes + no, pad = Math.max(1.5, sz * 0.1), r = rng(877);
      g.save();
      for (let i = 0; i < N; i++) {
        const col = Math.floor(i / rows), row = i % rows, x = gx + col * sz + pad / 2, y = gy + row * sz + pad / 2, w = sz - pad;
        const isYes = i < yes, vis = isYes ? i < ys : N - 1 - i < ns, j = (r() - 0.5) * sz * 0.08;
        if (!vis) { g.fillStyle = 'rgba(40,24,12,0.18)'; g.fillRect(x, y, w, w); continue; }
        g.fillStyle = 'rgba(30,18,8,0.35)'; g.fillRect(x + 1.5, y + 2, w, w);
        g.fillStyle = isYes ? c.navy : c.cream; g.fillRect(x + j, y - j, w, w);
      }
      g.restore();
    }
    /* the step-by-step working, written on the board (numeric cards) */
    drawWork(R) {
      const c = CC(), q = this.inst, steps = stepsOf(q.explain), t0 = this.workAt;
      Civic.parchment(R.x + R.w / 2, R.y + R.h / 2, R.w - 50, R.h - 50, { seed: 880, lines: 0, depth: 0.8 });
      label('THE WORKING', R.x + R.w / 2, R.y + 70, 32, { color: c.brick });
      const fs = UI.text(40), maxW = R.w - 190;
      let y = R.y + 140, tt = t0 + 0.2;
      steps.forEach((s, i) => {
        const L = UI._wrap(s, maxW, `${fs}px ${HAND}`);
        if (T >= tt) { g.save(); g.lineWidth = 3; g.strokeStyle = c.brick; g.beginPath(); g.arc(R.x + 86, y + fs * 0.1, fs * 0.46, 0, TAU); g.stroke();
          g.font = `${Math.round(fs * 0.6)}px ${SLAB}`; g.fillStyle = c.brick; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(String(i + 1), R.x + 86, y + fs * 0.12); g.restore(); }
        L.forEach(l => { const d = Math.max(0.25, l.length * 0.03); hand(l, R.x + 130, y, fs, tt, { align: 'left', dur: d, color: c.ink, seed: 881 + i }); tt += d; y += fs * 1.2; });
        y += fs * 0.35;
      });
    }

    /* ---- the right-hand card: narration / the question / the bill's summary ---- */
    drawCard(V) {
      const c = CC(), X0 = 902, W = 986, hs0 = UI.hudSlot(0), minTop = hs0.y + hs0.r + 16;
      const b = this.beat;
      if (this.phase === 'ask' || this.phase === 'answered') return this.drawAsk(X0, W, minTop);
      if (this.phase === 'billdone') return this.drawBillDone(X0, W, minTop);
      // a timed beat: what's happening + skip
      const top = 514, h = 552;
      paperRect(X0 + W / 2, top + h / 2, W, h, PAL.cream, { seed: 890, depth: 1.2, boil: false });
      label(`BILL ${this.bi + 1} OF ${this.n}`, X0 + W / 2, top + 50, 30, { color: c.brick });
      const fs = UI.text(52), L = UI._wrap(narration(b, this.bill), W - 110, `${fs}px ${HAND}`);
      L.forEach((l, i) => hand(l, X0 + W / 2, top + h / 2 - 30 + (i - (L.length - 1) / 2) * fs * 1.2, fs, this.beatAt + 0.05 + i * 0.2, { color: c.navy, seed: 891 + i, dur: 0.35 }));
      const bh = UI.tap(80), bw = Math.max(250, bh * 2.4);
      if (UI.button('bl-skip', (UI.keys ? 'Enter  ' : '') + 'Skip ▶▶', X0 + W - bw / 2 - 30, top + h - bh / 2 - 26, { w: bw, h: 80, color: c.navy })) this.skip();
    }
    askLayout(X0, W, minTop) {
      const q = this.inst, fq = UI.text(38), fo = UI.text(32), fw = UI.text(31), key = this.bi + '|' + this.k + '|' + fq + '|' + this.picks.length + '|' + this.phase;
      if (this._al && this._al.key === key) return this._al;
      const qL = UI._wrap(q.text, W - 80, `${fq}px ${HAND}`);
      const oL = q.options.map(o => UI._wrap(o, W - 190, `${fo}px ${HAND}`));
      const rh = oL.map(L => Math.round(Math.max(UI.tap(78), L.length * fo * 1.12 + 28)));
      const why = this.whyText(), wL = why ? UI._wrap('Judge O: ' + why, W - 80, `${fw}px ${HAND}`) : [];
      const bh = UI.tap(80), bw = Math.max(270, bh * 2.6);
      const rL = this.phase === 'answered' ? UI._wrap(this.resultText(), W - bw - 110, `${fw}px ${HAND}`) : [];
      const h = Math.round(26 + qL.length * fq * 1.16 + 16 + rh.reduce((a, x) => a + x + 8, 0) + (wL.length ? wL.length * fw * 1.18 + 14 : 0) + (rL.length ? Math.max(bh, rL.length * fw * 1.18) + 22 : 0) + 18);
      const top = Math.max(minTop, Math.min(514, 1068 - h));
      return (this._al = { key, qL, oL, rh, wL, rL, fq, fo, fw, bh, bw, h, top });
    }
    drawAsk(X0, W, minTop) {
      const c = CC(), q = this.inst, A = this.askLayout(X0, W, minTop), done = this.phase === 'answered', ai = q.answerIndex;
      const t0 = this.beatAt;
      paperRect(X0 + W / 2, A.top + A.h / 2, W, A.h, PAL.cream, { seed: 893, depth: 1.3, boil: false });
      let y = A.top + 26;
      A.qL.forEach((l, i) => hand(l, X0 + 40, y + (i + 0.5) * A.fq * 1.16, A.fq, t0 + 0.05 + i * 0.15, { align: 'left', color: c.ink, seed: 894 + i, dur: 0.3 }));
      y += A.qL.length * A.fq * 1.16 + 16;
      q.options.forEach((o, i) => {
        const h = A.rh[i], cy = y + h / 2, crossed = this.picks.includes(i) && i !== ai, right = done && i === ai;
        const hit = !done && !crossed ? UI._hit('bl-opt' + this.bi + '_' + this.k + '_' + i, X0 + W / 2, cy, W - 50, h) : { hover: false, pressed: false, clicked: false };
        const bg = right ? '#dfe6f2' : crossed ? '#e6d8c8' : hit.hover ? '#fffaf0' : PAL.white;
        paperRect(X0 + W / 2, cy + (hit.pressed ? 2 : hit.hover ? -3 : 0), W - 50, h - 4, bg, { seed: 900 + i, depth: hit.hover ? 1.4 : crossed ? 0.3 : 0.8, boil: false });
        const kx = X0 + 70;
        paperRect(kx, cy, 52, 52, right ? c.navy : crossed ? '#b8a894' : c.brick, { seed: 905 + i, depth: 0.4, boil: false });
        g.save(); g.font = `30px ${SLAB}`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = PAL.cream; g.fillText(String(i + 1), kx, cy + 2); g.restore();
        let mw = 0;
        A.oL[i].forEach((l, k) => { g.save(); g.font = `${A.fo}px ${HAND}`; mw = Math.max(mw, g.measureText(l).width); g.restore();
          hand(l, kx + 50, cy + (k - (A.oL[i].length - 1) / 2) * A.fo * 1.12, A.fo, t0 + 0.3 + i * 0.1, { align: 'left', color: crossed ? '#8a7a66' : c.ink, seed: 910 + i * 3 + k, dur: 0.25 }); });
        if (crossed) ink([[kx + 44, cy + 4], [kx + 60 + mw, cy - 4]], { w: 5, color: c.brick, seed: 920 + i, prog: P(T, this.pickedAt != null && i === this.picks[this.picks.length - 1] ? this.pickedAt : -1e6, (this.pickedAt || 0) + 0.3), step: 10, passes: 1 });
        if (right) ink([[X0 + W - 110, cy], [X0 + W - 90, cy + 20], [X0 + W - 58, cy - 22]], { w: 7, color: c.navy, seed: 925, prog: P(T, this.answeredAt, this.answeredAt + 0.3), step: 6 });
        if (hit.clicked) this.choose(i);
        y += h + 8;
      });
      if (A.wL.length) {
        A.wL.forEach((l, i) => hand(l, X0 + 40, y + 8 + (i + 0.5) * A.fw * 1.18, A.fw, (this.pickedAt || 0) + 0.2 + i * 0.2, { align: 'left', color: c.brick, seed: 930 + i, dur: 0.35 }));
        y += A.wL.length * A.fw * 1.18 + 14;
      }
      if (done) {
        const r = this.res(this.bi, this.beat.ask), bx = X0 + W - A.bw / 2 - 30, by = y + 8 + A.bh / 2;
        A.rL.forEach((l, i) => hand(l, X0 + 40, by + (i - (A.rL.length - 1) / 2) * A.fw * 1.18, A.fw, this.answeredAt + 0.1, { align: 'left', color: r && r.stars ? c.navy : c.brick, seed: 935 + i, dur: 0.3 }));
        if (Game.time - this.answeredAt >= NEXT_GUARD && UI.button('bl-next', (UI.keys ? 'Enter  ' : '') + 'Continue ▶', bx, by, { w: A.bw, h: 80, color: c.navy })) this.next();
      }
    }
    drawBillDone(X0, W, minTop) {
      const c = CC(), bill = this.bill, res = this.results[this.bi] || [], t0 = this.beatAt, out = bill.journey.outcome;
      const top = Math.max(minTop, 514), h = 1068 - top;
      paperRect(X0 + W / 2, top + h / 2, W, h, PAL.cream, { seed: 940, depth: 1.3, boil: false });
      const word = out === 'law' ? 'LAW' : out === 'struck' ? 'STRUCK DOWN' : 'DEAD';
      Civic.woodtype(word, X0 + W / 2, top + 70, 54, { color: out === 'law' ? c.navy : c.brick, ink: PAL.cream, t0: t0 + 0.1, seed: 941 });
      hand('The ' + titleCase(bill.title), X0 + W / 2, top + 140, UI.text(38), t0 + 0.3, { color: c.ink, seed: 942 });
      const got = res.reduce((a, r) => a + (r ? r.stars : 0), 0);
      for (let i = 0; i < bill.asks.length; i++) doodleStar(X0 + W / 2 + (i - (bill.asks.length - 1) / 2) * 100, top + 225, 38, { color: res[i] && res[i].stars ? PAL.mustard : '#cdbfa8', seed: 943 + i, s: pop(T, t0 + 0.4 + i * 0.15, 0.3) });
      hand(`${got} of ${bill.asks.length} stars on this bill · ${plural(this.stars, 'star')} so far`, X0 + W / 2, top + 300, UI.text(34), t0 + 0.6, { color: c.navy, seed: 946 });
      const bh = UI.tap(90), label_ = this.bi < this.n - 1 ? 'Next bill ▶' : 'See my stars ▶';
      if (Game.time - this.beatAt >= NEXT_GUARD && UI.button('bl-nextbill', (UI.keys ? 'Enter  ' : '') + label_, X0 + W / 2, Math.min(top + h - bh / 2 - 30, top + 400), { w: 420, h: 90, color: c.brick })) this.next();
    }

    /* ---- portrait: the compact tally card on the stage, everything else in the dock ---- */
    drawTallyCompact(V) {
      const c = CC(), R = { x: 40, y: 726, w: 1840, h: 340 }, t = V.tally;
      paperRect(R.x + R.w / 2, R.y + R.h / 2, R.w, R.h, '#a97c4f', { seed: 960, depth: 1.3, boil: false });
      if (!t) {
        const L = UI._wrap(this.bill.title, R.w - 200, `76px ${TYPE}`);
        g.save(); g.font = `76px ${TYPE}`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = PAL.cream;
        L.forEach((l, i) => g.fillText(l, R.x + R.w / 2, R.y + R.h / 2 + (i - (L.length - 1) / 2) * 90)); g.restore();
        return;
      }
      const st = t.st, ch = chamberOf(st.stage), ov = isOverride(st.stage), N = st.yes + st.no;
      const ys = Math.min(st.yes, Math.floor(t.p * st.yes + 1e-9)), ns = Math.min(st.no, Math.floor(t.p * st.no + 1e-9));
      label((ch === 'house' ? 'HOUSE' : 'SENATE') + (ov ? ' OVERRIDE' : '') + ' VOTE · ' + N + ' VOTING', R.x + R.w / 2, R.y + 44, 46, { color: PAL.cream });
      Civic.woodtype('YES ' + ys, R.x + R.w * 0.28, R.y + 140, 84, { color: c.navy, ink: PAL.cream, seed: 961 });
      Civic.woodtype('NO ' + ns, R.x + R.w * 0.72, R.y + 140, 84, { color: c.cream, ink: c.navy, seed: 962 });
      // the bar: yes from the left, no from the right
      const bx = R.x + 80, bw = R.w - 160, by = R.y + 230, bhh = 48;
      g.save(); g.fillStyle = 'rgba(40,24,12,0.3)'; g.fillRect(bx, by, bw, bhh);
      g.fillStyle = c.navy; g.fillRect(bx, by, bw * ys / N, bhh);
      g.fillStyle = c.cream; g.fillRect(bx + bw * (1 - ns / N), by, bw * ns / N, bhh); g.restore();
      const ln = V.lines[st.stage];
      if (ln) {
        const need = ov ? Civics.twoThirdsNeeded(N) : Civics.majorityNeeded(N), lx = bx + bw * need / N;
        ink([[lx, by - 18], [lx, by + bhh + 18]], { w: 8, color: c.brick, seed: 963, prog: P(T, ln.t0, ln.t0 + 0.4), passes: 1 });
        hand((ov ? 'two-thirds: ' : 'majority: ') + need + (st.result && st.result.startsWith('tie') ? (st.result === 'tie-vp-yes' ? '   VP votes YES' : '   VP votes NO') : ''), Math.max(R.x + 330, Math.min(R.x + R.w - 330, lx)), by + bhh + 50, 48, ln.t0 + 0.2, { color: PAL.cream, seed: 964, outline: c.brick });
      }
    }
    drawDock() {
      const q = this.inst, b = this.beat, id = this.bi + '_' + this.k;
      if (this.phase === 'ask' || this.phase === 'answered') {
        const done = this.phase === 'answered', ai = q.answerIndex;
        const state = q.options.map((o, i) => (done ? (i === ai ? 'right' : this.picks.includes(i) ? 'wrong' : 'faded') : this.picks.includes(i) ? 'wrong' : ''));
        Dock.choices('bl-q' + id, { prompt: q.text, options: q.options, state, locked: done, onChoose: i => this.choose(i) });
        const why = this.whyText();
        if (why) Dock.text('bl-why' + id + '_' + this.picks.length, ['Judge O: ' + why], { cls: 'note', reveal: true });
        if (this.showWork()) Dock.list('bl-work' + id, stepsOf(q.explain).map((t, k) => ({ badge: String(k + 1), color: CC().brick, lines: ['Step ' + (k + 1), t] })));
        if (done) {
          const r = this.res(this.bi, b.ask);
          Dock.text('bl-res' + id, [this.resultText()], { cls: r && r.stars ? 'right' : 'wrong', reveal: true });
          Dock.button({ id: 'next', glyph: 'Continue ▶', label: 'continue', aria: 'continue', go: true, onClick: () => this.next() });
        }
        return;
      }
      if (this.phase === 'billdone') {
        const res = this.results[this.bi] || [], got = res.reduce((a, r) => a + (r ? r.stars : 0), 0), out = this.bill.journey.outcome;
        Dock.heading('bl-bd' + id, (out === 'law' ? 'Law! ' : out === 'struck' ? 'Struck down. ' : 'Dead. ') + 'The ' + titleCase(this.bill.title));
        Dock.text('bl-bd' + id, [`${got} of ${this.bill.asks.length} stars on this bill. ${plural(this.stars, 'star')} so far.`]);
        Dock.button({ id: 'next', glyph: this.bi < this.n - 1 ? 'Next bill ▶' : 'See my stars ▶', label: 'next', aria: 'next bill', go: true, onClick: () => this.next() });
        return;
      }
      Dock.text('bl-narr' + id, [narration(b, this.bill)], { cls: 'note' });
      Dock.button({ id: 'skip', glyph: 'Skip ▶▶', label: 'skip', aria: 'skip the animation', onClick: () => this.skip() });
    }

    /* ---- intro and end ---- */
    drawIntro(docked) {
      const c = CC(), t0 = this.enteredAt, pass = this.passStars, of = this.of;
      const lines = [`${this.n} bills. Follow each one from the House to the President's desk.`, '3 decisions per bill. Count the votes.', 'A tie fails. Two-thirds means two-thirds.', `${pass} of ${of} stars to sign it.`];
      Civic.bunting(560, 1760, 6, { sag: 26, n: 13, seed: 7, t0: t0 + 0.1 });
      UI.hud('PASS THE BILL', '');
      const rise = E.outBack(P(T, t0 + 0.2, t0 + 0.7), 1.4);
      Puppet.draw('judge', 300, 1080 + (1 - rise) * 500, docked ? 820 : 700, { facing: 1, t: T, env: this.t > 0.6 && this.t < 2.4 ? 0.25 + 0.25 * Math.sin(T * 17) : 0 });
      if (docked) {
        Civic.parchment(1180, 560, 1300, 760, { seed: 971, depth: 1.6 });
        Civic.woodtype('PASS THE BILL', 1180, 400, 96, { color: c.navy, ink: PAL.cream, t0: t0 + 0.2, seed: 972 });
        scroll(1180, 660, 2.2, 'CLEAN BEACHES ACT', { rot: -0.05 });
        Dock.heading('bl-intro', 'Pass the Bill', { cls: 'big' });
        Dock.text('bl-intro', lines);
        Dock.button({ id: 'go', glyph: 'Start ▶', label: 'start', aria: 'Start: the first bill', go: true, color: 'red', onClick: () => this.begin() });
        return;
      }
      if (this.t > 0.6) UI.speech('Every vote counts. So count them.', 430, 330, { side: 'left', w: 460, t0: t0 + 0.6 });
      Civic.parchment(1230, 580, 1160, 760, { seed: 971, depth: 1.6 });
      tape(700, 205, 160, -0.5, undefined, 3); tape(1760, 205, 160, 0.5, undefined, 4);
      Civic.woodtype('PASS THE BILL', 1230, 300, 76, { color: c.navy, ink: PAL.cream, t0: t0 + 0.2, seed: 972 });
      const fs = UI.text(40);
      let y = 420;
      lines.forEach((l, i) => UI._wrap(l, 1000, `${fs}px ${HAND}`).forEach(tx => { hand(tx, 1230, y, fs, t0 + 0.5 + i * 0.3, { color: i === 3 ? c.brick : c.ink, seed: 973 + i, dur: 0.5 }); y += fs * 1.35; }));
      if (this.t > 0.8 && UI.button('bl-start', (UI.keys ? 'Enter  ' : '') + 'Start ▶', 1230, Math.min(890, y + 90), { w: 420, h: 110, color: c.brick })) this.begin();
    }
    drawEnd(docked) {
      const c = CC(), s = this.stars, of = this.of, btns = this.endButtons();
      const rows = this.bills.map((b, i) => { const r = this.results[i] || [], got = r.reduce((a, x) => a + (x ? x.stars : 0), 0); return { b, got, out: b.journey.outcome }; });
      const word = o => (o === 'law' ? 'law' : o === 'struck' ? 'struck down' : 'dead');
      UI.hud('PASS THE BILL', '');
      if (docked) {
        Civic.woodtype(`${s} / ${of}`, 960, 420, 200, { color: c.navy, ink: PAL.cream, t0: this.endAt + 0.1, seed: 981 });
        UI.stamp(this.passed ? 'SIGNED' : 'TRY AGAIN', 960, 760, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.6, color: this.passed ? c.navy : c.brick });
        Dock.heading('bl-end', `Your stars: ${s} / ${of}`, { cls: 'big' });
        Dock.text('bl-end', [this.passed ? 'Passed! Collect your stamp.' : `Not yet: ${this.passStars} stars for the stamp. New bills?`]);
        Dock.list('bl-end', rows.map((x, i) => ({ badge: String(i + 1), color: x.got === x.b.asks.length ? c.navy : c.brick, lines: ['The ' + titleCase(x.b.title) + ' (' + word(x.out) + ')', `${x.got} of ${x.b.asks.length} stars`] })));
        Dock.text('bl-code', [`Quiz code ${this.seed}`], { cls: 'small' });
        Dock.buttons('bl-end', btns.map((b, i) => ({ label: b.label, wide: i === 0, onClick: b.go })), { bottom: true });
        return;
      }
      Puppet.draw('judge', 230, 1080, 560, { facing: 1, t: T, env: T - this.endAt < 2 ? 0.2 + 0.2 * Math.sin(T * 15) : 0 });
      UI.speech(this.passed ? 'Motion carried. Nobody rounded down.' : 'Recount! New bills, same rules.', 330, 380, { side: 'left', w: 440, t0: this.endAt + 0.6 });
      Civic.woodtype(`${s} / ${of}`, 900, 240, 110, { color: c.navy, ink: PAL.cream, t0: this.endAt + 0.1, seed: 981 });
      UI.stamp(this.passed ? 'SIGNED' : 'TRY AGAIN', 1450, 240, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.1, color: this.passed ? c.navy : c.brick });
      const fs = UI.text(36), rh = Math.max(84, fs * 1.9), top = 380;
      Civic.parchment(1190, top + rows.length * rh / 2 + 10, 1300, rows.length * rh + 60, { seed: 982, depth: 1.2 });
      rows.forEach((x, i) => {
        const y = top + 20 + (i + 0.5) * rh;
        hand(`${i + 1}. The ${titleCase(x.b.title)}: ${word(x.out)}`, 580, y, fs, this.endAt + 0.3 + i * 0.1, { align: 'left', color: c.ink, seed: 983 + i, dur: 0.3 });
        for (let k = 0; k < x.b.asks.length; k++) doodleStar(1640 + k * 64, y, 26, { color: this.results[i] && this.results[i][k] && this.results[i][k].stars ? PAL.mustard : '#cdbfa8', seed: 990 + i * 3 + k, s: pop(T, this.endAt + 0.5 + i * 0.1, 0.3) });
      });
      const py = top + rows.length * rh + 90;
      hand(this.passed ? 'Passed! Collect your stamp.' : `Not yet: ${this.passStars} stars for the stamp.`, 1190, py, UI.text(40), this.endAt + 0.8, { color: c.brick, seed: 995 });
      const bh = UI.tap(100), by = Math.min(1080 - 30 - bh / 2, py + 50 + bh / 2);
      btns.forEach((b, i) => { if (this.t > 0.2 && UI.button('bl-end' + i, b.label, 930 + i * 520, by, { w: 480, h: 100, color: b.color })) b.go(); });
      hand(`quiz code ${this.seed}`, 1880, 1050, UI.text(26), this.endAt + 0.6, { align: 'right', color: c.navy, seed: 996 });
    }
  }
  Bill.STEP = STEP; Bill.beatsOf = beatsOf; Bill.narration = narration;
  Bill.solution = () => { const s = Game.current(); return s instanceof Bill ? s.solution : null; };
  Bill.options = () => UI.hitRects.filter(r => /^bl-opt/.test(r.id)).map(r => ({ id: r.id, i: +r.id.split('_').pop(), x: r.x, y: r.y, w: r.w, h: r.h }));
  Levels.bill = Bill;
})();
